// src/components/billing/QuotationTable.jsx - Updated with working actions
import React from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const QuotationTable = ({ quotations, loading, onRefresh }) => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN');
  };

  const handleDeleteQuotation = async (quotationId) => {
    if (!window.confirm('Are you sure you want to delete this quotation? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'quotations', quotationId));
      showAlert('Quotation deleted successfully', 'success');
      onRefresh();
    } catch (error) {
      console.error('Error deleting quotation:', error);
      showAlert('Failed to delete quotation', 'error');
    }
  };

  const handleSendWhatsApp = (quotation) => {
    try {
      const companyName = vendorProfile?.shopName || 'Your Company';
      const quotationUrl = `${window.location.origin}/billing/quotation/${quotation.id}`;
      const itemsCount = quotation.items?.length || 0;
      const totalQuantity = quotation.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
     
      const message = encodeURIComponent(
        `Dear ${quotation.customerName || 'Customer'},

Thank you for your inquiry!

*Quotation Details:*
📋 Quotation Number: ${quotation.quotationNumber}
🏪 From: ${companyName}
💰 Amount: ${formatCurrency(quotation.totalAmount)}
📅 Date: ${formatDate(quotation.createdAt)}
📦 Items: ${itemsCount} (Total Qty: ${totalQuantity})
${quotation.quotationType === 'with_gst' ? '🧾 GST Quotation' : '📄 Non-GST Quotation'}

View Quotation: ${quotationUrl}

Please contact us for any queries or to proceed with the order.
Best regards,
${companyName}`
      );

      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      showAlert('Failed to generate WhatsApp link', 'error');
    }
  };

  const handlePrint = async (quotation) => {
    // Load bank details before generating print content
    const bankAccounts = await loadBankDetailsForPrint();
    const primaryBank = bankAccounts.find(account => account.isPrimary) || bankAccounts[0];
    
    // Create a printable version of the quotation
    const printContent = generatePrintableContent(quotation, primaryBank);
    
    // Create a new window with specific dimensions (A4 size approximation)
    const printWindow = window.open('', '_blank', 'width=800,height=1000,scrollbars=yes,resizable=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          // Don't auto-close, let user close manually after printing
        }, 500);
      };
    } else {
      showAlert('Please allow popups to print quotations', 'warning');
    }
  };

  const loadBankDetailsForPrint = async () => {
    try {
      if (vendorProfile?.uid) {
        const bankCollection = collection(db, 'vendors', vendorProfile.uid, 'bankAccounts');
        const bankSnapshot = await getDocs(bankCollection);
        const accounts = bankSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort accounts to put primary account first
        accounts.sort((a, b) => {
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return 0;
        });

        return accounts;
      }
      return [];
    } catch (error) {
      console.error('Error loading bank accounts for print:', error);
      return [];
    }
  };

  const generatePrintableContent = (quotation, primaryBank = null) => {
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
      }).format(amount || 0);
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const calculateCGSTSGST = (taxableAmount, gstRate) => {
      const totalGSTAmount = (taxableAmount * gstRate) / 100;
      const cgstAmount = totalGSTAmount / 2;
      const sgstAmount = totalGSTAmount / 2;
      return { cgstAmount, sgstAmount, totalGSTAmount };
    };

    const calculateItemTotalWithGST = (item, discountPercentage = 0) => {
      const itemDiscountAmount = (item.amount * discountPercentage) / 100;
      const itemTaxableAmount = item.amount - itemDiscountAmount;
      if (quotation.quotationType === 'with_gst') {
        const { totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
        return itemTaxableAmount + totalGSTAmount;
      }
      return itemTaxableAmount;
    };

    const discountPercentage = parseFloat(quotation.discount) || 0;
    const discountAmount = (quotation.subtotal * discountPercentage) / 100;

    // Generate GST breakdown
    const gstBreakdown = {};
    quotation.items?.forEach(item => {
      const rate = item.gstRate || 0;
      const itemTaxableAmount = (item.amount || 0) - ((item.amount || 0) * discountPercentage) / 100;
      const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, rate);

      if (rate > 0) {
        if (!gstBreakdown[rate]) {
          gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
        }
        gstBreakdown[rate].taxableAmount += itemTaxableAmount;
        gstBreakdown[rate].cgstAmount += cgstAmount;
        gstBreakdown[rate].sgstAmount += sgstAmount;
        gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
      }
    });

    return `
<!DOCTYPE html>
<html>
<head>
    <title>Quotation - ${quotation.quotationNumber}</title>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 20px; 
            font-size: 12px; 
            line-height: 1.4;
            max-width: 800px;
            margin: 0 auto;
            background: white;
        }
        .header { display: flex; justify-content: space-between; margin-bottom: 30px; align-items: flex-start; }
        .company-info { text-align: right; max-width: 300px; }
        .company-info h2 { font-size: 18px; margin-bottom: 8px; color: #1f2937; }
        .quotation-title { font-size: 28px; font-weight: bold; color: #7c3aed; margin-bottom: 10px; }
        .quotation-details { margin-bottom: 10px; }
        .quotation-details p { margin-bottom: 4px; }
        .customer-section { margin-bottom: 30px; }
        .customer-section h3 { font-size: 16px; margin-bottom: 10px; color: #1f2937; }
        .customer-box { 
            background-color: #f9fafb; 
            padding: 15px; 
            border-radius: 5px; 
            border: 1px solid #e5e7eb;
        }
        .customer-box p { margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
        th, td { border: 1px solid #d1d5db; padding: 6px; text-align: left; }
        th { background-color: #f9fafb; font-weight: bold; font-size: 10px; text-transform: uppercase; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .totals-section { display: flex; justify-content: space-between; margin-bottom: 30px; gap: 20px; }
        .bank-details, .totals { width: 48%; }
        .bank-details { 
            background-color: #eff6ff; 
            padding: 15px; 
            border-radius: 5px; 
            border: 1px solid #bfdbfe; 
        }
        .bank-details h5 { 
            font-size: 14px; 
            margin-bottom: 10px; 
            color: #1e40af; 
            display: flex; 
            align-items: center; 
        }
        .totals { 
            background-color: #f9fafb; 
            padding: 15px; 
            border-radius: 5px; 
            border: 1px solid #d1d5db; 
        }
        .totals table { margin: 0; font-size: 12px; }
        .totals table td { border: none; padding: 4px 0; }
        .total-row { font-weight: bold; font-size: 14px; border-top: 2px solid #6b7280; }
        .total-row td { padding-top: 8px; border-top: 2px solid #6b7280; }
        .notes-section { margin-bottom: 20px; }
        .notes-section h5 { font-size: 14px; margin-bottom: 8px; color: #1f2937; }
        .notes-section p { font-size: 12px; line-height: 1.5; }
        .footer { 
            text-align: center; 
            color: #6b7280; 
            border-top: 1px solid #d1d5db; 
            padding-top: 20px; 
            margin-top: 30px;
        }
        .badge { 
            display: inline-block; 
            padding: 2px 8px; 
            background-color: #e0e7ff; 
            color: #3730a3; 
            border-radius: 12px; 
            font-size: 10px; 
            font-weight: 500; 
        }
        
        /* Print specific styles */
        @media print { 
            body { margin: 0; padding: 15px; }
            .header { page-break-after: avoid; }
            .customer-section { page-break-after: avoid; }
            table { page-break-inside: avoid; }
            .totals-section { page-break-inside: avoid; }
            @page { margin: 1cm; }
        }
        
        /* Screen display styles */
        @media screen {
            body { 
                box-shadow: 0 0 20px rgba(0,0,0,0.1); 
                margin: 20px auto; 
                background: white;
                min-height: calc(100vh - 40px);
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div>
            <div class="quotation-title">QUOTATION</div>
            <div class="quotation-details">
                <p><strong>Quotation #:</strong> ${quotation.quotationNumber}</p>
                <p><strong>Date:</strong> ${formatDate(quotation.createdAt)}</p>
                <p><strong>Status:</strong> <span class="badge">${quotation.status?.toUpperCase() || 'ACTIVE'}</span></p>
            </div>
        </div>
        ${quotation.quotationType === 'with_gst' && vendorProfile ? `
        <div class="company-info">
            <h2>${vendorProfile.shopName || 'Your Shop Name'}</h2>
            <p>${vendorProfile.address || 'Shop Address'}</p>
            <p>${vendorProfile.city || 'City'}, ${vendorProfile.state || 'State'} ${vendorProfile.pincode || 'PIN'}</p>
            <p>Phone: ${vendorProfile.phone || 'N/A'}</p>
            <p>Email: ${vendorProfile.email || 'N/A'}</p>
            ${vendorProfile.gstNumber ? `<p><strong>GST:</strong> ${vendorProfile.gstNumber}</p>` : ''}
        </div>
        ` : ''}
    </div>

    <div class="customer-section">
        <h3>Quote To:</h3>
        <div class="customer-box">
            <p><strong>${quotation.customerName}</strong></p>
            ${quotation.customerEmail ? `<p>${quotation.customerEmail}</p>` : ''}
            ${quotation.customerPhone ? `<p>${quotation.customerPhone}</p>` : ''}
            ${quotation.quotationType === 'with_gst' && quotation.customerGST ? `<p><strong>GST:</strong> ${quotation.customerGST}</p>` : ''}
            ${quotation.customerAddress ? `<p style="margin-top: 8px;">${quotation.customerAddress}</p>` : ''}
        </div>
    </div>

    <h4 style="font-size: 16px; margin-bottom: 15px; color: #1f2937;">Quotation Items</h4>
    <table>
        <thead>
            <tr>
                <th style="width: 25%;">Description</th>
                <th class="text-center" style="width: 8%;">HSN</th>
                <th class="text-center" style="width: 8%;">Qty</th>
                <th class="text-right" style="width: 12%;">Rate (₹)</th>
                <th class="text-center" style="width: 8%;">GST (%)</th>
                <th class="text-right" style="width: 10%;">CGST (₹)</th>
                <th class="text-right" style="width: 10%;">SGST (₹)</th>
                <th class="text-right" style="width: 12%;">Amount (₹)</th>
                <th class="text-right" style="width: 12%;">Total (₹)</th>
            </tr>
        </thead>
        <tbody>
            ${quotation.items?.filter(item => item.name?.trim() && item.quantity > 0 && item.rate > 0).map(item => {
              const itemDiscountAmount = (item.amount * discountPercentage) / 100;
              const itemTaxableAmount = item.amount - itemDiscountAmount;
              const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
              const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

              return `
                <tr>
                    <td>
                        <strong>${item.name}</strong>
                        ${item.productId ? `<br><small style="color: #6b7280;">ID: ${item.productId}</small>` : ''}
                    </td>
                    <td class="text-center">${item.hsnCode || 'N/A'}</td>
                    <td class="text-center">${item.quantity}</td>
                    <td class="text-right">${formatCurrency(item.rate)}</td>
                    <td class="text-center">${item.gstRate || 0}%</td>
                    <td class="text-right">${formatCurrency(cgstAmount)}</td>
                    <td class="text-right">${formatCurrency(sgstAmount)}</td>
                    <td class="text-right">${formatCurrency(item.amount)}</td>
                    <td class="text-right"><strong>${formatCurrency(totalWithGST)}</strong></td>
                </tr>
              `;
            }).join('')}
        </tbody>
    </table>

    <div class="totals-section">
        <div class="bank-details">
            <h5>💳 Bank Details</h5>
            ${primaryBank ? `
            <p><strong>Account Holder:</strong> ${primaryBank.accountHolderName || 'N/A'}</p>
            <p><strong>Account Number:</strong> ${primaryBank.accountNumber || 'N/A'}</p>
            <p><strong>IFSC Code:</strong> ${primaryBank.ifscCode || 'N/A'}</p>
            <p><strong>Bank Name:</strong> ${primaryBank.bankName || 'N/A'}</p>
            <p><strong>Branch:</strong> ${primaryBank.branchName || 'N/A'}</p>
            <p><strong>Account Type:</strong> ${primaryBank.accountType ? primaryBank.accountType.charAt(0).toUpperCase() + primaryBank.accountType.slice(1) : 'N/A'}</p>
            ${primaryBank.upiId ? `<p><strong>UPI ID:</strong> ${primaryBank.upiId}</p>` : ''}
            ` : `
            <p style="color: #6b7280; font-style: italic;">No bank details available</p>
            <p style="font-size: 11px; color: #6b7280;">Please add bank details in your profile settings</p>
            `}
        </div>
        
        <div class="totals">
            <table>
                <tr>
                    <td>Subtotal:</td>
                    <td class="text-right">${formatCurrency(quotation.subtotal)}</td>
                </tr>
                ${discountPercentage > 0 ? `
                <tr>
                    <td>Discount (${discountPercentage}%):</td>
                    <td class="text-right" style="color: #059669;">-${formatCurrency(discountAmount)}</td>
                </tr>
                ` : ''}
                ${quotation.quotationType === 'with_gst' && Object.keys(gstBreakdown).length > 0 ? `
                <tr>
                    <td colspan="2" style="padding-top: 8px; border-top: 1px solid #d1d5db;"><strong>GST Breakdown:</strong></td>
                </tr>
                ${Object.entries(gstBreakdown).map(([rate, data]) => `
                <tr>
                    <td style="padding-left: 10px; font-size: 11px;">CGST @ ${rate / 2}%:</td>
                    <td class="text-right" style="font-size: 11px;">${formatCurrency(data.cgstAmount)}</td>
                </tr>
                <tr>
                    <td style="padding-left: 10px; font-size: 11px;">SGST @ ${rate / 2}%:</td>
                    <td class="text-right" style="font-size: 11px;">${formatCurrency(data.sgstAmount)}</td>
                </tr>
                `).join('')}
                <tr>
                    <td><strong>Total GST:</strong></td>
                    <td class="text-right"><strong>${formatCurrency(quotation.totalTaxAmount)}</strong></td>
                </tr>
                ` : ''}
                <tr class="total-row">
                    <td><strong>TOTAL:</strong></td>
                    <td class="text-right"><strong style="color: #7c3aed;">${formatCurrency(quotation.totalAmount)}</strong></td>
                </tr>
            </table>
        </div>
    </div>

    ${quotation.notes || quotation.terms ? `
    <div class="notes-section">
        ${quotation.notes ? `
        <div style="margin-bottom: 15px;">
            <h5>Notes:</h5>
            <p>${quotation.notes}</p>
        </div>
        ` : ''}
        ${quotation.terms ? `
        <div>
            <h5>Terms & Conditions:</h5>
            <p>${quotation.terms}</p>
        </div>
        ` : ''}
    </div>
    ` : ''}

    <div class="footer">
        <p><strong>Thank you for considering our quotation!</strong></p>
        ${quotation.quotationType === 'with_gst' ? '<p style="margin-top: 5px; font-size: 11px;">This is a computer generated quotation.</p>' : ''}
    </div>

    <script>
        window.onload = function() {
            setTimeout(function() {
                window.print();
            }, 500);
        };
    </script>
</body>
</html>
    `;
  };

  const getQuotationTypeBadge = (quotationType) => {
    if (quotationType === 'with_gst') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          GST
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Non-GST
      </span>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Quotation Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Items & Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created Date
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
                <p className="mt-2 text-sm text-gray-500">Loading quotations...</p>
              </td>
            </tr>
          ) : quotations.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                <div className="flex flex-col items-center py-8">
                  <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-900 mb-1">No quotations found</p>
                  <p className="text-sm text-gray-500">Create your first quotation to get started.</p>
                </div>
              </td>
            </tr>
          ) : (
            quotations.map((quotation) => (
              <tr key={quotation.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <Link
                      to={`/billing/quotation/${quotation.id}`}
                      className="text-purple-600 hover:text-purple-900 font-medium"
                    >
                      {quotation.quotationNumber}
                    </Link>
                    {quotation.notes && (
                      <div className="text-xs text-gray-400 mt-1 truncate max-w-xs">
                        Note: {quotation.notes}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {quotation.customerName}
                    </div>
                    {quotation.customerEmail && (
                      <div className="text-sm text-gray-500">
                        {quotation.customerEmail}
                      </div>
                    )}
                    {quotation.customerPhone && (
                      <div className="text-sm text-gray-500">
                        {quotation.customerPhone}
                      </div>
                    )}
                    {quotation.customerGST && (
                      <div className="text-xs text-blue-600 font-medium">
                        GST: {quotation.customerGST}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(quotation.totalAmount)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {quotation.items?.length || 0} items
                    </div>
                    <div className="text-sm text-gray-500">
                      Qty: {quotation.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                    </div>
                    {(quotation.discount != null && parseFloat(quotation.discount) > 0) && (
                      <div className="text-xs text-green-600">
                        Discount: {parseFloat(quotation.discount)}%
                      </div>
                    )}
                    {quotation.quotationType === 'with_gst' && quotation.totalTaxAmount > 0 && (
                      <div className="text-xs text-gray-500">
                        GST: {formatCurrency(quotation.totalTaxAmount)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    {getQuotationTypeBadge(quotation.quotationType)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="text-sm text-purple-600">
                    {formatDate(quotation.createdAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`/billing/quotation/${quotation.id}`}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all duration-200"
                        title="View Quotation"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handlePrint(quotation)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700 transition-all duration-200"
                        title="Print Quotation"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleSendWhatsApp(quotation)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-all duration-200"
                        title="Send via WhatsApp"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteQuotation(quotation.id)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200"
                        title="Delete Quotation"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default QuotationTable;